import React, { useState, useRef, useCallback } from 'react';
import { toPng } from 'html-to-image';
import { Loader2, Download, Plus, X, ImagePlus } from 'lucide-react';

// Brand colours
const C = {
  yellow: '#FFD93D',
  blue: '#4389BD',
  lightBlue: '#ADD4EA',
  cream: '#FFFBF0',
  lightYellow: '#FFF3B0',
  gold: '#D4A843',
  white: '#FFFFFF',
};

// VK Monogram watermark — yellow circle, blue V, light yellow K
function VKMonogram({ size = 48, opacity = 0.3 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity }}>
      <circle cx="100" cy="100" r="92" fill={C.yellow} />
      <text x="85" y="134" textAnchor="middle" fontFamily="'Cormorant Garamond', Georgia, serif" fontSize="110" fontWeight="600" fill={C.blue}>V</text>
      <text x="130" y="134" textAnchor="middle" fontFamily="'Cormorant Garamond', Georgia, serif" fontSize="110" fontWeight="600" fill={C.lightYellow}>K</text>
    </svg>
  );
}

// Lux Libris logo watermark — cross + book motif in brand colours
function LuxLibrisLogo({ size = 48, opacity = 0.3 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity }}>
      <circle cx="100" cy="100" r="92" fill={C.cream} />
      {/* Cross */}
      <rect x="92" y="40" width="16" height="80" rx="4" fill={C.gold} />
      <rect x="68" y="64" width="64" height="16" rx="4" fill={C.gold} />
      {/* Open book below cross */}
      <path d="M60 135 Q100 125 100 155 Q100 125 140 135 L140 165 Q100 155 100 175 Q100 155 60 165 Z" fill={C.lightBlue} stroke={C.blue} strokeWidth="3" />
      {/* Book spine */}
      <line x1="100" y1="125" x2="100" y2="175" stroke={C.blue} strokeWidth="2" />
    </svg>
  );
}

// Get the correct watermark component for a template category
function CategoryWatermark({ category, size = 48 }) {
  if (category === 'Professional IG') return <VKMonogram size={size} opacity={0.3} />;
  if (category === 'Lux Libris IG') return <LuxLibrisLogo size={size} opacity={0.3} />;
  return null; // LinkedIn — no watermark
}

// ── Template Definitions ──

const TEMPLATES = [
  // Professional IG
  {
    id: 'pro-photo-feature',
    name: 'Photo Feature',
    category: 'Professional IG',
    width: 1080,
    height: 1080,
    fields: [
      { key: 'heading', label: 'Heading', default: 'Today in the Library' },
      { key: 'subheading', label: 'Subheading', default: '' },
    ],
    hasPhoto: true,
  },
  {
    id: 'pro-quote-card',
    name: 'Quote Card',
    category: 'Professional IG',
    width: 1080,
    height: 1080,
    fields: [
      { key: 'quote', label: 'Quote', default: '"The library is the heart of the school."', multiline: true },
      { key: 'attribution', label: 'Attribution', default: 'Dr. Verity Kahn' },
    ],
    hasPhoto: false,
  },
  {
    id: 'pro-announcement',
    name: 'Announcement',
    category: 'Professional IG',
    width: 1080,
    height: 1080,
    fields: [
      { key: 'heading', label: 'Heading', default: 'Exciting News!' },
      { key: 'subheading', label: 'Details', default: 'Something wonderful is happening...', multiline: true },
    ],
    hasPhoto: false,
  },
  // Lux Libris IG
  {
    id: 'lux-saint-reveal',
    name: 'Saint Reveal',
    category: 'Lux Libris IG',
    width: 1080,
    height: 1080,
    fields: [
      { key: 'heading', label: 'Saint Name', default: 'New Saint Unlocked!' },
      { key: 'subheading', label: 'Subtitle', default: 'Forming Saints, One Book at a Time' },
    ],
    hasPhoto: true,
  },
  {
    id: 'lux-milestone',
    name: 'Milestone',
    category: 'Lux Libris IG',
    width: 1080,
    height: 1080,
    fields: [
      { key: 'number', label: 'Number / Stat', default: '1,000' },
      { key: 'heading', label: 'Milestone Label', default: 'Books Read!' },
      { key: 'subheading', label: 'Subtitle', default: 'Our readers are on fire' },
    ],
    hasPhoto: false,
  },
  {
    id: 'lux-feature-spotlight',
    name: 'Feature Spotlight',
    category: 'Lux Libris IG',
    width: 1080,
    height: 1080,
    fields: [
      { key: 'heading', label: 'Feature Name', default: 'New Feature' },
      { key: 'body', label: 'Description', default: 'Discover what Lux Libris can do...', multiline: true },
    ],
    hasPhoto: true,
  },
  // LinkedIn
  {
    id: 'li-thought-leadership',
    name: 'Thought Leadership',
    category: 'LinkedIn',
    width: 1200,
    height: 627,
    fields: [
      { key: 'headline', label: 'Headline', default: 'What if every school had a museum?', multiline: true },
    ],
    hasPhoto: false,
  },
  {
    id: 'li-project-showcase',
    name: 'Project Showcase',
    category: 'LinkedIn',
    width: 1200,
    height: 627,
    fields: [
      { key: 'heading', label: 'Project Name', default: 'Mini-Museum' },
      { key: 'subheading', label: 'Tagline', default: 'Bringing museum-quality experiences to every student' },
    ],
    hasPhoto: true,
  },
];

// ── Template Renderers ──
// Each renders at actual pixel size inside a scaled container.
// Inline styles are required because html-to-image cannot read Tailwind classes.

function TemplateProPhotoFeature({ fields, photo, showLogo, category }) {
  return (
    <div style={{ width: 1080, height: 1080, position: 'relative', background: '#1e293b', fontFamily: "'Source Serif 4', Georgia, serif" }}>
      <div style={{ width: '100%', height: '70%', overflow: 'hidden', background: '#0f172a' }}>
        {photo ? (
          <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 48 }}>
            Photo Area
          </div>
        )}
      </div>
      <div style={{ width: '100%', height: '30%', background: C.yellow, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 64px' }}>
        <p style={{ fontSize: 52, fontWeight: 700, color: '#1e293b', margin: 0, fontFamily: "'Cormorant Garamond', Georgia, serif", lineHeight: 1.2 }}>
          {fields.heading}
        </p>
        {fields.subheading && (
          <p style={{ fontSize: 32, color: '#374151', margin: '12px 0 0', lineHeight: 1.3 }}>{fields.subheading}</p>
        )}
      </div>
      {showLogo && <div style={{ position: 'absolute', bottom: 24, right: 24 }}><CategoryWatermark category={category} size={56} /></div>}
    </div>
  );
}

function TemplateProQuoteCard({ fields, showLogo, category }) {
  return (
    <div style={{ width: 1080, height: 1080, position: 'relative', background: C.cream, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 80, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <div style={{ width: 80, height: 4, background: C.blue, marginBottom: 48 }} />
      <p style={{ fontSize: 56, color: '#1e293b', textAlign: 'center', lineHeight: 1.4, fontWeight: 300, margin: 0, whiteSpace: 'pre-wrap' }}>
        {fields.quote}
      </p>
      <div style={{ width: 80, height: 4, background: C.blue, margin: '48px 0 32px' }} />
      <p style={{ fontSize: 28, color: C.blue, fontFamily: "'Source Serif 4', Georgia, serif", fontWeight: 600, margin: 0 }}>
        {fields.attribution}
      </p>
      {showLogo && <div style={{ position: 'absolute', bottom: 32, right: 32 }}><CategoryWatermark category={category} size={48} /></div>}
    </div>
  );
}

function TemplateProAnnouncement({ fields, showLogo, category }) {
  return (
    <div style={{ width: 1080, height: 1080, position: 'relative', background: C.yellow, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 80, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <p style={{ fontSize: 72, fontWeight: 700, color: C.blue, margin: 0, lineHeight: 1.2 }}>
        {fields.heading}
      </p>
      {fields.subheading && (
        <p style={{ fontSize: 36, color: '#1e293b', margin: '32px 0 0', lineHeight: 1.5, fontFamily: "'Source Serif 4', Georgia, serif", whiteSpace: 'pre-wrap' }}>
          {fields.subheading}
        </p>
      )}
      {showLogo && <div style={{ position: 'absolute', bottom: 32, right: 32 }}><CategoryWatermark category={category} size={48} /></div>}
    </div>
  );
}

function TemplateLuxSaintReveal({ fields, photo, showLogo, category }) {
  return (
    <div style={{ width: 1080, height: 1080, position: 'relative', background: C.lightBlue, display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <div style={{ paddingTop: 56, textAlign: 'center' }}>
        <p style={{ fontSize: 22, color: C.blue, fontFamily: "'Source Serif 4', Georgia, serif", letterSpacing: 4, textTransform: 'uppercase', margin: '0 0 8px' }}>Lux Libris</p>
        <p style={{ fontSize: 52, fontWeight: 700, color: '#1e293b', margin: 0, lineHeight: 1.2 }}>{fields.heading}</p>
      </div>
      <div style={{ flex: 1, width: '70%', margin: '32px 0', overflow: 'hidden', borderRadius: 24, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        {photo ? (
          <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ color: '#94a3b8', fontSize: 36 }}>Saint Image</div>
        )}
      </div>
      <div style={{ paddingBottom: 48, textAlign: 'center' }}>
        <p style={{ fontSize: 28, color: '#374151', fontFamily: "'Source Serif 4', Georgia, serif", margin: 0, fontStyle: 'italic' }}>{fields.subheading}</p>
      </div>
      {showLogo && <div style={{ position: 'absolute', bottom: 24, right: 24 }}><CategoryWatermark category={category} size={48} /></div>}
    </div>
  );
}

function TemplateLuxMilestone({ fields, showLogo, category }) {
  return (
    <div style={{ width: 1080, height: 1080, position: 'relative', background: C.white, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <p style={{ fontSize: 22, color: C.gold, fontFamily: "'Source Serif 4', Georgia, serif", letterSpacing: 4, textTransform: 'uppercase', margin: '0 0 24px' }}>Lux Libris</p>
      <div style={{ width: 120, height: 4, background: C.gold, marginBottom: 48 }} />
      <p style={{ fontSize: 140, fontWeight: 700, color: '#1e293b', margin: 0, lineHeight: 1 }}>{fields.number}</p>
      <p style={{ fontSize: 52, fontWeight: 600, color: C.gold, margin: '16px 0 0' }}>{fields.heading}</p>
      <div style={{ width: 120, height: 4, background: C.gold, margin: '48px 0 32px' }} />
      <p style={{ fontSize: 28, color: '#64748b', fontFamily: "'Source Serif 4', Georgia, serif", margin: 0 }}>{fields.subheading}</p>
      {showLogo && <div style={{ position: 'absolute', bottom: 32, right: 32 }}><CategoryWatermark category={category} size={48} /></div>}
    </div>
  );
}

function TemplateLuxFeatureSpotlight({ fields, photo, showLogo, category }) {
  return (
    <div style={{ width: 1080, height: 1080, position: 'relative', background: '#f8fafc', fontFamily: "'Source Serif 4', Georgia, serif" }}>
      <div style={{ height: 6, background: `linear-gradient(90deg, ${C.lightBlue}, ${C.gold})` }} />
      <div style={{ padding: '48px 64px 24px', textAlign: 'center' }}>
        <p style={{ fontSize: 22, color: C.blue, letterSpacing: 4, textTransform: 'uppercase', margin: '0 0 12px' }}>Lux Libris</p>
        <p style={{ fontSize: 48, fontWeight: 700, color: '#1e293b', margin: 0, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{fields.heading}</p>
      </div>
      <div style={{ margin: '0 64px', height: 480, borderRadius: 16, overflow: 'hidden', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
        {photo ? (
          <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ color: '#94a3b8', fontSize: 32 }}>Screenshot Area</div>
        )}
      </div>
      <div style={{ padding: '32px 80px', textAlign: 'center' }}>
        <p style={{ fontSize: 28, color: '#475569', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{fields.body}</p>
      </div>
      {showLogo && <div style={{ position: 'absolute', bottom: 24, right: 24 }}><CategoryWatermark category={category} size={48} /></div>}
    </div>
  );
}

function TemplateLiThoughtLeadership({ fields, showLogo, category }) {
  return (
    <div style={{ width: 1200, height: 627, position: 'relative', background: C.yellow, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 96px', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <p style={{ fontSize: 64, fontWeight: 700, color: C.blue, margin: 0, lineHeight: 1.3, whiteSpace: 'pre-wrap' }}>
        {fields.headline}
      </p>
      <div style={{ position: 'absolute', bottom: 40, left: 96, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 48, height: 4, background: C.blue }} />
        <p style={{ fontSize: 20, color: '#374151', fontFamily: "'Source Serif 4', Georgia, serif", margin: 0 }}>Dr. Verity Kahn, PhD</p>
      </div>
      {showLogo && <div style={{ position: 'absolute', bottom: 32, right: 32 }}><CategoryWatermark category={category} size={48} /></div>}
    </div>
  );
}

function TemplateLiProjectShowcase({ fields, photo, showLogo, category }) {
  return (
    <div style={{ width: 1200, height: 627, position: 'relative', background: '#f8fafc', display: 'flex', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <div style={{ width: '50%', height: '100%', overflow: 'hidden', background: '#1e293b' }}>
        {photo ? (
          <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 36 }}>Photo</div>
        )}
      </div>
      <div style={{ width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 56px' }}>
        <div style={{ width: 64, height: 4, background: C.yellow, marginBottom: 24 }} />
        <p style={{ fontSize: 48, fontWeight: 700, color: '#1e293b', margin: 0, lineHeight: 1.2 }}>{fields.heading}</p>
        <p style={{ fontSize: 24, color: '#475569', margin: '20px 0 0', fontFamily: "'Source Serif 4', Georgia, serif", lineHeight: 1.5 }}>{fields.subheading}</p>
        <div style={{ width: 64, height: 4, background: C.blue, marginTop: 32 }} />
      </div>
      {showLogo && <div style={{ position: 'absolute', bottom: 20, right: 24 }}><CategoryWatermark category={category} size={44} /></div>}
    </div>
  );
}

const RENDERERS = {
  'pro-photo-feature': TemplateProPhotoFeature,
  'pro-quote-card': TemplateProQuoteCard,
  'pro-announcement': TemplateProAnnouncement,
  'lux-saint-reveal': TemplateLuxSaintReveal,
  'lux-milestone': TemplateLuxMilestone,
  'lux-feature-spotlight': TemplateLuxFeatureSpotlight,
  'li-thought-leadership': TemplateLiThoughtLeadership,
  'li-project-showcase': TemplateLiProjectShowcase,
};

// Category colours for template grid
const CATEGORY_COLORS = {
  'Professional IG': { bg: '#FFD93D', text: '#1e293b' },
  'Lux Libris IG': { bg: '#ADD4EA', text: '#1e293b' },
  'LinkedIn': { bg: '#4389BD', text: '#ffffff' },
};

// ── Main Component ──

export default function ImageGenerator({ onAddToPost }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fields, setFields] = useState({});
  const [templatePhoto, setTemplatePhoto] = useState(null);
  const [showLogo, setShowLogo] = useState(true);
  const [exporting, setExporting] = useState(false);

  const renderRef = useRef(null);
  const photoInputRef = useRef(null);

  const selectTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    const defaults = {};
    template.fields.forEach(f => { defaults[f.key] = f.default; });
    setFields(defaults);
    setTemplatePhoto(null);
  }, []);

  const updateField = (key, value) => {
    setFields(prev => ({ ...prev, [key]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setTemplatePhoto(URL.createObjectURL(file));
      e.target.value = '';
    }
  };

  const exportPng = async () => {
    if (!renderRef.current) return;
    setExporting(true);
    try {
      // html-to-image needs the element visible and at full size
      const dataUrl = await toPng(renderRef.current, {
        width: selectedTemplate.width,
        height: selectedTemplate.height,
        pixelRatio: 1,
        style: {
          transform: 'none',
          transformOrigin: 'top left',
        },
      });
      return dataUrl;
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = async () => {
    const dataUrl = await exportPng();
    if (!dataUrl) return;
    const link = document.createElement('a');
    link.download = `${selectedTemplate.id}-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleAddToPost = async () => {
    const dataUrl = await exportPng();
    if (!dataUrl) return;
    // Convert data URL to File for the compose panel
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const file = new File([blob], `${selectedTemplate.id}.png`, { type: 'image/png' });
    onAddToPost(file);
  };

  const Renderer = selectedTemplate ? RENDERERS[selectedTemplate.id] : null;

  // Scale factor for preview
  const previewScale = selectedTemplate
    ? Math.min(1, 480 / selectedTemplate.width)
    : 1;

  return (
    <div className="space-y-6">
      {/* Template Selector or Editor */}
      {!selectedTemplate ? (
        <TemplateGrid templates={TEMPLATES} onSelect={selectTemplate} />
      ) : (
        <div className="space-y-4">
          {/* Back to templates */}
          <button
            onClick={() => setSelectedTemplate(null)}
            className="text-sm text-slate-400 hover:text-slate-200 transition flex items-center gap-1"
          >
            <X size={14} /> Back to templates
          </button>

          {/* Live Preview */}
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-700 overflow-hidden">
            <div
              style={{
                width: selectedTemplate.width,
                height: selectedTemplate.height,
                transform: `scale(${previewScale})`,
                transformOrigin: 'top left',
              }}
            >
              <div ref={renderRef} style={{ width: selectedTemplate.width, height: selectedTemplate.height }}>
                <Renderer
                  fields={fields}
                  photo={templatePhoto}
                  showLogo={showLogo}
                  category={selectedTemplate.category}
                />
              </div>
            </div>
            {/* Spacer to account for scaled size */}
            <div style={{ height: selectedTemplate.height * previewScale - selectedTemplate.height, marginTop: selectedTemplate.height * (previewScale - 1) }} />
          </div>

          {/* Editor Fields */}
          <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 space-y-3">
            {selectedTemplate.fields.map(f => (
              <div key={f.key}>
                <label className="text-xs text-slate-400 font-medium block mb-1">{f.label}</label>
                {f.multiline ? (
                  <textarea
                    value={fields[f.key] || ''}
                    onChange={(e) => updateField(f.key, e.target.value)}
                    className="w-full bg-slate-900 text-slate-200 text-sm rounded-lg p-2 border border-slate-600 focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                ) : (
                  <input
                    type="text"
                    value={fields[f.key] || ''}
                    onChange={(e) => updateField(f.key, e.target.value)}
                    className="w-full bg-slate-900 text-slate-200 text-sm rounded-lg p-2 border border-slate-600 focus:ring-2 focus:ring-rose-500 focus:border-transparent"
                  />
                )}
              </div>
            ))}

            {/* Photo upload for templates that support it */}
            {selectedTemplate.hasPhoto && (
              <div>
                <label className="text-xs text-slate-400 font-medium block mb-1">Photo</label>
                {templatePhoto ? (
                  <div className="flex items-center gap-2">
                    <img src={templatePhoto} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-600" />
                    <button
                      onClick={() => setTemplatePhoto(null)}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    className="w-full py-2 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 text-sm hover:border-slate-500 hover:text-slate-300 transition flex items-center justify-center gap-2"
                  >
                    <ImagePlus size={16} /> Upload photo
                  </button>
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
            )}

            {/* Logo toggle — only for categories that have watermarks */}
            {selectedTemplate.category !== 'LinkedIn' && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showLogo}
                onChange={(e) => setShowLogo(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 text-rose-500 focus:ring-rose-500 bg-slate-900"
              />
              <span className="text-xs text-slate-400">
                Show {selectedTemplate.category === 'Lux Libris IG' ? 'Lux Libris logo' : 'VK monogram'}
              </span>
            </label>
            )}
          </div>

          {/* Export buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              disabled={exporting}
              className="flex-1 py-2.5 rounded-lg font-medium text-sm transition flex items-center justify-center gap-2 bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-50"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Download PNG
            </button>
            <button
              onClick={handleAddToPost}
              disabled={exporting}
              className="flex-1 py-2.5 rounded-lg font-medium text-sm transition flex items-center justify-center gap-2 bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500 disabled:opacity-50"
            >
              {exporting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              Add to Post
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Template Grid ──

function TemplateGrid({ templates, onSelect }) {
  const categories = ['Professional IG', 'Lux Libris IG', 'LinkedIn'];

  return (
    <div className="space-y-5">
      {categories.map(cat => {
        const catTemplates = templates.filter(t => t.category === cat);
        const colors = CATEGORY_COLORS[cat];
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: colors.bg, color: colors.text }}
              >
                {cat}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {catTemplates.map(t => {
                const Renderer = RENDERERS[t.id];
                const defaults = {};
                t.fields.forEach(f => { defaults[f.key] = f.default; });
                const thumbScale = 120 / t.width;
                return (
                  <button
                    key={t.id}
                    onClick={() => onSelect(t)}
                    className="bg-slate-900 rounded-lg border border-slate-700 hover:border-slate-500 transition overflow-hidden text-left group"
                  >
                    <div className="overflow-hidden" style={{ height: t.height * thumbScale }}>
                      <div style={{
                        width: t.width,
                        height: t.height,
                        transform: `scale(${thumbScale})`,
                        transformOrigin: 'top left',
                        pointerEvents: 'none',
                      }}>
                        <Renderer fields={defaults} showLogo={false} />
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 group-hover:text-slate-200 transition p-2 truncate">{t.name}</p>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
